import { useCallback, useEffect, useState } from 'react'
import { useChatGPT } from './useChatGPT'
import { IPromptInjection } from 'constants/interfaces'

interface UseConversationModalOptions {
  autoSendFirstUserMessage?: boolean
  onError?: () => void
  onReInitialize?: () => void
  referrer?: number | null
  preserveHistory?: boolean // Keep conversation history
  prompt_id?: string // <-- add prompt_id
  group_id?: string // <-- if needed
  isInjectReports?: boolean
  isInstantConversation?: boolean // Send first empty message and fetch reports for second message
  mode?: number // 0 - text, 1 - voice
  play?: boolean // Controls voice playback (only for mode 1)
  promptInjections?: IPromptInjection[]
  defaultOpen?: boolean // Start the modal already open (for on-demand flows)
}

export const useConversationModal = (options: UseConversationModalOptions = {}) => {
  const [isOpen, setIsOpen] = useState(options.defaultOpen ?? false)
  const [hasWebSocketError, setHasWebSocketError] = useState(false)
  const [hasBeenOpened, setHasBeenOpened] = useState(options.defaultOpen ?? false)

  const {
    conversation,
    setConversation,
    sendMessage,
    isBotResponseInProgress,
    isWebSocketReady,
    disconnectWebSocket,
    resetSessionId,
    isSessionExpired,
    reloadSession,
    stopVoicePlayback,
  } = useChatGPT({
    enabled: isOpen && hasBeenOpened, // WebSocket is only enabled after the first opening
    isInjectReports: options.isInjectReports || false,
    isInstantConversation: options.isInstantConversation || false,
    mode: options.mode || 0,
    play: options.play ?? true, // Default to true if not specified
    onError: () => {
      console.error('WebSocket error occurred')
      setHasWebSocketError(true)
      options.onError?.()
    },
    onReInitialize: () => {
      console.log('WebSocket reinitialized due to timeout')
      setHasWebSocketError(false)
      options.onReInitialize?.()
    },

    group_id: options.group_id,
    ...(options.prompt_id ? { prompt_id: options.prompt_id } : {}),
    promptInjections: options.promptInjections,
  })

  // Cleaning when the modal is closed
  useEffect(() => {
    if (!isOpen && hasBeenOpened) {
      disconnectWebSocket()
      stopVoicePlayback() // Stop voice playback when modal closes
      // Do not reset session_id when closing ConversationModal
      // session_id will be reset only when the AIPop is closed
      if (options.preserveHistory === false) {
        setConversation([])
      }
      setHasWebSocketError(false)
    }
  }, [isOpen, hasBeenOpened, disconnectWebSocket, setConversation, options.preserveHistory, stopVoicePlayback])

  const openModal = useCallback(() => {
    console.log('Opening modal, WebSocket should connect...')
    setHasWebSocketError(false)
    setHasBeenOpened(true) // Mark that the modal was opened
    setIsOpen(true)
  }, [])

  const closeModal = useCallback(() => {
    console.log('Closing modal, WebSocket should disconnect...')
    setIsOpen(false)
  }, [])

  const clearHistory = useCallback(() => {
    console.log('Clearing conversation history...')
    resetSessionId()
    setConversation([])
  }, [resetSessionId, setConversation])

  const handleSendMessage = useCallback(
    (message: string) => {
      if (isSessionExpired) {
        console.log('Cannot send message - session expired')
        return
      }

      if (message.trim()) {
        // Add user message to the conversation
        setConversation((prev) => [...prev, { message: message.trim(), isUserMessage: true }])

        sendMessage({
          text: message.trim(),
          is_init: conversation.length === 0, // First message in the conversation
          group_id: options.group_id,
        })
      }
    },
    [conversation.length, options.group_id, sendMessage, setConversation, isSessionExpired]
  )

  const handleReconnect = useCallback(() => {
    console.log('Attempting to reconnect WebSocket...')
    setHasWebSocketError(false)
    // Simply close and open the modal for reconnection
    closeModal()
    setTimeout(() => {
      openModal()
    }, 1000)
  }, [closeModal, openModal])

  return {
    // Modal state
    isOpen,
    openModal,
    closeModal,
    // Conversation data
    conversation,
    isBotResponseInProgress,
    isWebSocketReady,
    hasWebSocketError,
    isSessionExpired,
    // Methods
    handleSendMessage,
    disconnectWebSocket,
    resetSessionId,
    clearHistory,
    handleReconnect,
    reloadSession,
    stopVoicePlayback,
  }
}
